import { z } from 'zod';
import { clinicCidSchema } from './clinicRecords.js';
/**
 * Área do médico, etapa M2 (rodada 15): documentos em PDF. Atestado, declaração de
 * comparecimento, pedido de exames (com pacotes), relatório e termo. Emitido não muda (como
 * o prontuário): errou, cancela com o motivo e emite outro. Vai para o paciente pela Saúde
 * e pelo WhatsApp (um link que vale 30 dias). A declaração de comparecimento qualquer pessoa
 * da equipe emite; os outros, só quem tem a permissão do dado clínico.
 */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD');
const isoDateTime = z.iso.datetime();
const hhmm = z.string().regex(/^\d{2}:\d{2}$/);
export const CLINIC_DOCUMENT_KINDS = [
    'atestado',
    'comparecimento',
    'exames',
    'relatorio',
    'termo',
];
export const clinicDocumentKindSchema = z.enum(CLINIC_DOCUMENT_KINDS);
/**
 * O que cada área pode emitir. Personal não dá atestado médico nem pede exame (é ato de
 * médico); declara comparecimento, faz relatório e colhe termo.
 */
export function clinicDocumentKindsFor(specialty) {
    return specialty === 'educacao_fisica'
        ? CLINIC_DOCUMENT_KINDS.filter((kind) => kind !== 'atestado' && kind !== 'exames')
        : [...CLINIC_DOCUMENT_KINDS];
}
export const CLINIC_DOCUMENT_INFO = {
    atestado: {
        label: 'Atestado',
        title: 'Atestado médico',
        clinical: true,
        hint: 'Afastamento por dias, com CID só se a pessoa autorizar.',
    },
    comparecimento: {
        label: 'Declaração de comparecimento',
        title: 'Declaração de comparecimento',
        clinical: false,
        hint: 'Que a pessoa esteve na clínica, com o horário.',
    },
    exames: {
        label: 'Pedido de exames',
        title: 'Solicitação de exames',
        clinical: true,
        hint: 'Lista de exames, com os pacotes da clínica.',
    },
    relatorio: {
        label: 'Relatório',
        title: 'Relatório',
        clinical: true,
        hint: 'Texto livre: evolução, encaminhamento, laudo.',
    },
    termo: {
        label: 'Termo',
        title: 'Termo de consentimento',
        clinical: true,
        hint: 'O que a pessoa leu e aceita, com as duas assinaturas.',
    },
};
export const createClinicDocumentSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('atestado'),
        day: isoDate,
        days: z.number().int().min(1).max(365),
        /** CID no atestado só com a autorização da pessoa (CFM). */
        cid: clinicCidSchema.optional(),
        cidAuthorized: z.boolean().optional(),
        registration: z.string().trim().max(40).optional(),
        appointmentId: z.uuid().optional(),
    }),
    z.object({
        kind: z.literal('comparecimento'),
        day: isoDate,
        from: hhmm,
        to: hhmm,
        reason: z.string().trim().max(80).optional(),
        registration: z.string().trim().max(40).optional(),
        appointmentId: z.uuid().optional(),
    }),
    z.object({
        kind: z.literal('exames'),
        exams: z.array(z.string().trim().min(2).max(120)).min(1).max(60),
        indication: z.string().trim().max(400).optional(),
        registration: z.string().trim().max(40).optional(),
        appointmentId: z.uuid().optional(),
    }),
    z.object({
        kind: z.literal('relatorio'),
        title: z.string().trim().min(2).max(80).optional(),
        text: z.string().trim().min(10).max(8000),
        registration: z.string().trim().max(40).optional(),
        appointmentId: z.uuid().optional(),
    }),
    z.object({
        kind: z.literal('termo'),
        title: z.string().trim().min(2).max(80),
        text: z.string().trim().min(10).max(8000),
        registration: z.string().trim().max(40).optional(),
        appointmentId: z.uuid().optional(),
    }),
]);
export const clinicDocumentSchema = z.object({
    id: z.uuid(),
    patientId: z.uuid(),
    kind: clinicDocumentKindSchema,
    title: z.string(),
    /** O texto que vai no PDF (a lista, no pedido de exames). */
    body: z.string(),
    author: z.string(),
    registration: z.string().nullable(),
    issuedAt: isoDateTime,
    /** Código curto impresso no rodapé, para conferir. */
    code: z.string(),
    voidedAt: isoDateTime.nullable(),
    voidReason: z.string().nullable(),
});
export const voidClinicDocumentSchema = z.object({
    reason: z.string().trim().min(3).max(200),
});
export const clinicDocumentShareSchema = z.object({
    url: z.string(),
    expiresAt: isoDateTime,
});
/** O que a pessoa vê na Saúde: o documento e quando saiu (o PDF baixa à parte). */
export const saudeDocumentSchema = z.object({
    id: z.uuid(),
    title: z.string(),
    issuedAt: isoDateTime,
    author: z.string(),
});
/** Pacotes de exames de fábrica (a clínica cria os dela). */
export const CLINIC_EXAM_PACKS = [
    {
        key: 'basico',
        title: 'Check-up básico',
        exams: [
            'Hemograma completo',
            'Glicemia de jejum',
            'Hemoglobina glicada (HbA1c)',
            'Colesterol total e frações',
            'Triglicerídeos',
            'TGO (AST)',
            'TGP (ALT)',
            'Creatinina',
            'Ureia',
            'TSH',
            'Urina tipo 1 (EAS)',
        ],
    },
    {
        key: 'metabolico',
        title: 'Metabólico',
        exams: [
            'Glicemia de jejum',
            'Insulina de jejum',
            'Hemoglobina glicada (HbA1c)',
            'Colesterol total e frações',
            'Triglicerídeos',
            'Ácido úrico',
            'TGO (AST)',
            'TGP (ALT)',
            'Gama-GT',
            'Vitamina D (25-OH)',
            'Ferritina',
        ],
    },
    {
        key: 'hormonal_masculino',
        title: 'Hormonal masculino',
        exams: [
            'Testosterona total',
            'Testosterona livre (calculada)',
            'SHBG',
            'Estradiol',
            'LH',
            'FSH',
            'Prolactina',
            'PSA total',
            'Hemograma completo',
        ],
    },
    {
        key: 'tireoide',
        title: 'Tireoide',
        exams: ['TSH', 'T4 livre', 'Anti-TPO'],
    },
];
/** Um pacote de exames: os de fábrica (acima) e os que a clínica guardou. */
export const clinicExamPackSchema = z.object({
    key: z.string(),
    title: z.string(),
    exams: z.array(z.string()),
    custom: z.boolean(),
});
export const saveClinicExamPackSchema = z.object({
    title: z.string().trim().min(2).max(60),
    exams: z.array(z.string().trim().min(2).max(120)).min(1).max(60),
});
